import { getAccountByDomain } from '~/utils/dataProviders/AccountApiDataProvider';
import { isCached, resolveCurrentDomainHost } from '~/utils/helpers';
import { useAccountStore } from '~/stores/accountStore';
import { DEFAULT_APP_COLOR, DEFAULT_BUTTON_COLOR, DEFAULT_BUTTON_TEXT_COLOR, DEFAULT_LINK_COLOR } from '~/utils/constants';
import type { AccountDetailResponse } from '~/types/adminApi';
import { account as accountMock } from '~/server/mocks/account';
import { handleRequestWithMock } from '~/composables/handleRequestWithMock';
import { useJwtTokenStore } from '~/stores/jwtTokenStore';

export async function loadAccount() {
    const store = useAccountStore();
    const nuxtApp = useNuxtApp();
    const tokens = useJwtTokenStore();

    if (isCached(store.state.loadedAt)) {
        return;
    }

    store.setIsLoading(true);

    let account: AccountDetailResponse;
    try {
        account = await handleRequestWithMock<AccountDetailResponse>(
            () => getAccountByDomain(resolveCurrentDomainHost()),
            accountMock
        );
    } catch (error) {
        (nuxtApp as any).$sentrySetContext?.('tokenDebugInfo', {
            fieldName: 'getMrAdminApiToken',
            clientTime: Date.now(),
            fullTokenValue: tokens.getMrAdminApiToken,
            tokenLength: tokens.getMrAdminApiToken?.length,
        });
        
        (nuxtApp as any).$sentryAddBreadcrumb?.({
            message: 'Token debug info',
            category: 'debug',
            level: 'info',
            data: {
                clientTime: Date.now(),
                fullTokenValue: tokens.getMrAdminApiToken
            }
        });
        
        (nuxtApp as any).$sentryCaptureException?.(new Error('Token Debug Info', {
            cause: {
                fieldName: 'getMrAdminApiToken',
                fullTokenValue: tokens.getMrAdminApiToken
            }
        }));
        throw error;
    }

    store.setAccountState({
        id: account.id,
        name: account.name,
        slug: account.slug,
        appCityId: account.appCityId,
        logo: account.logo,
        background: account.background,
        web: account.web,
        locale: account.locale,
        appColor: !account.colorScheme?.appColor ? DEFAULT_APP_COLOR : account.colorScheme.appColor,
        buttonColor: !account.colorScheme?.buttonColor ? DEFAULT_BUTTON_COLOR : account.colorScheme.buttonColor,
        buttonTextColor: !account.colorScheme?.buttonTextColor ? DEFAULT_BUTTON_TEXT_COLOR : account.colorScheme.buttonTextColor,
        linkColor: !account.colorScheme?.linkColor ? DEFAULT_LINK_COLOR : account.colorScheme.linkColor,
        registrationLink: account.registrationLink,
        headScript: account.myMrLite?.headScript || '',
        bodyScript: account.myMrLite?.bodyScript || '',
        loadedAt: Date.now(),
    });

    store.setIsLoading(false);
}
